import { green } from "@mui/material/colors";
import { keyframes, styled } from "@mui/material/styles";
import React, { JSX, useEffect } from "react";
import TextChange from "work/proposals/redlining/__diff/text-change";
import DiffChangePopover from "work/proposals/redlining/__diff/view/diff-change-popover";

const settleAddedText = keyframes`
  0%, 70% {
    font-weight: 500;
  }
  100% {
    font-weight: 400;
  }
`;

const collapseRemovedText = keyframes`
  0%, 60% {
    max-width: 24rem;
    opacity: 1;
    font-size: inherit;
    line-height: inherit;
    margin-right: 0;
  }
  100% {
    max-width: 0;
    opacity: 0;
    font-size: 0;
    line-height: 0;
    margin-right: 0;
  }
`;

const DiffResult = styled("span", {
  shouldForwardProp: (prop) => !["change", "allResolved", "acceptedResolved", "animateAcceptedResolved"].includes(prop.toString()),
})<{
  change?: TextChange;
  allResolved?: boolean;
  acceptedResolved?: boolean;
  animateAcceptedResolved?: boolean;
}>(
  ({change, allResolved, acceptedResolved, animateAcceptedResolved, theme}) => ({
    backgroundColor: (function () {
      if (allResolved) return "unset";
      if (change?.diff.added) return green[300];
    })(),
    color: (function () {
      if (change?.diff.removed && acceptedResolved) return theme.palette.text.disabled;
      if (change?.diff.added && acceptedResolved) return theme.palette.text.primary;
      if (change?.diff.added && allResolved) return theme.palette.primary.main;
      return theme.palette.text.primary;
    })(),
    fontWeight: (function () {
      if (acceptedResolved && change?.diff.added) return 500;
      if (acceptedResolved && (change?.diff.removed || change?.isModified)) return 500;
      return "inherit";
    })(),
    fontSynthesis: (function (): string | undefined {
      if (acceptedResolved && change?.diff.added) return "weight";
      return undefined;
    })(),
    display: change?.diff.removed && animateAcceptedResolved ? "inline-block" : "inline",
    overflow: change?.diff.removed && animateAcceptedResolved ? "hidden" : "visible",
    verticalAlign: "bottom",
    textDecoration: (function () {
      if (change?.diff.removed) return "line-through";
    })(),
    textDecorationColor: (function () {
      if (change?.diff.removed && acceptedResolved) return theme.palette.text.primary;
      if (change?.diff.removed && allResolved) return theme.palette.primary.main;
      if (change?.diff.removed) return theme.palette.error.main;
      return "initial";
    })(),
    textDecorationThickness: '2px',
    '&:hover': {
      textDecorationColor: (function() { 
        let color = "initial";
        if (change?.diff.removed && acceptedResolved) color = theme.palette.text.primary;
        if (change?.diff.removed && allResolved) color = theme.palette.primary.main;
        if (change?.diff.removed && !allResolved) color = theme.palette.error.main;
        return `oklch(from ${color} l c h / 0.25)`
      }())
    },
    animation: (function (): string | undefined {
      if (animateAcceptedResolved && change?.diff.added) return `${settleAddedText} 1s ease-out forwards`;
      if (animateAcceptedResolved && change?.diff.removed) return `${collapseRemovedText} 1s ease-out forwards`;
      return undefined;
    })(),
  }));
const TextContainer = styled("div")(
  ({theme}) => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    lineHeight: 1.5,
    paddingRight: theme.spacing(2),
    width: "100%",
  }));
const ChangeGroup = styled("span")(
  () => ({
    whiteSpace: "inherit"
  }));

export interface DiffTextProps {
  className?: string;
  textChanges: TextChange[];
  disablePopover: boolean;
  allResolved?: boolean;
  acceptedResolved?: boolean;
  animateAcceptedResolved?: boolean;
  onAcceptChange?: (change: TextChange) => void;
  onRejectChange?: (change: TextChange) => void;
  onEditChange?: (change: TextChange, newValue: string) => void;
  onPopoverChange?: (isOpen: boolean) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

export default function DiffText(props: Readonly<DiffTextProps>) {
  const {
    className,
    textChanges: changes,
    disablePopover,
    allResolved,
    acceptedResolved,
    animateAcceptedResolved,
    onAcceptChange,
    onRejectChange,
    onEditChange,
    onPopoverChange,
    onClick
  } = props;

  const [currentChange, setCurrentChange] = React.useState<TextChange | undefined>(
    changes[0]
  );
  const [popoverAnchorElement, setPopoverAnchorElement] =
    React.useState<Element | null>(null);
  const [isPopoverEditing, setIsPopoverEditing] = React.useState<boolean>(false);

  useEffect(() => {
    if (!disablePopover && (!onAcceptChange || !onRejectChange || !onEditChange)) {
      throw new Error("onAcceptChange, onRejectChange, onEditChange are required when disablePopover is false");
    }
  }, [disablePopover, onAcceptChange, onEditChange, onRejectChange]);

  const getChangeKey = (change: TextChange): string => {
    let changeType: string;
    if (change.diff.added) {
      changeType = "added";
    } else if (change.diff.removed) {
      changeType = "removed";
    } else {
      changeType = "unchanged";
    }
    return `${change.id}_${change?.diff.value}_${changeType}`;
  };

  function handleDiffPopoverOpen(event: React.MouseEvent, change: TextChange) {
    if (
      isPopoverEditing ||
      disablePopover ||
      allResolved ||
      change?.parent?.allChangesResolved ||
      (!change.isModified && !change.isAdded && !change.isRemoved)
    )
      return;

    setPopoverAnchorElement(event.currentTarget);
    setCurrentChange(change);
    onPopoverChange?.(true);
  }

  function handleDiffPopoverClose() {
    if (isPopoverEditing) return;
    setPopoverAnchorElement(null);
    setIsPopoverEditing(false);
    onPopoverChange?.(false);
  }

  function renderChanges(changes: TextChange[]) {
    let nodes: JSX.Element[] = [];

    for (let changeIndex = 0; changeIndex < changes.length; changeIndex++) {
      if (
        changes?.[changeIndex - 1]?.isModified &&
        changes[changeIndex].isModified
      ) {
        continue;
      }
      if (
        changes[changeIndex].isModified &&
        changes[changeIndex + 1]?.isModified
      ) {
        const node = (
          <ChangeGroup
            key={getChangeKey(changes[changeIndex + 1])}
            onMouseEnter={(event) => {
              handleDiffPopoverOpen(
                event,
                changes[changeIndex]
              );
            }}
          >
            <DiffResult
              change={changes[changeIndex]}
              allResolved={allResolved}
              acceptedResolved={acceptedResolved}
              animateAcceptedResolved={animateAcceptedResolved}
            >
              {changes[changeIndex]?.diff.value}
            </DiffResult>
            <DiffResult
              change={changes[changeIndex + 1]}
              allResolved={allResolved}
              acceptedResolved={acceptedResolved}
              animateAcceptedResolved={animateAcceptedResolved}
            >
              {changes[changeIndex + 1]?.diff.value}
            </DiffResult>
          </ChangeGroup>
        );
        nodes.push(node);
        continue;
      }

      const node = (
        <DiffResult
          key={getChangeKey(changes[changeIndex])}
          change={changes[changeIndex]}
          allResolved={allResolved}
          acceptedResolved={acceptedResolved}
          animateAcceptedResolved={animateAcceptedResolved}
          onMouseEnter={(event) => {
            handleDiffPopoverOpen(
              event,
              changes[changeIndex]
            );
          }}
        >
          {changes[changeIndex]?.diff.value}
        </DiffResult>
      );
      nodes.push(node);
    }

    return <span>{nodes.map((node) => node)}</span>;
  }

  return (
    <TextContainer className={className} onMouseLeave={handleDiffPopoverClose} onClick={onClick}>
      {renderChanges(changes)}
      {currentChange && !disablePopover && popoverAnchorElement && document.contains(popoverAnchorElement) && (
        <DiffChangePopover
          valueType="text"
          anchorElement={popoverAnchorElement}
          change={currentChange}
          onClosePopover={handleDiffPopoverClose}
          onEditingChange={setIsPopoverEditing}
          onAccept={onAcceptChange}
          onReject={onRejectChange}
          onEdit={onEditChange}
        />
      )}
    </TextContainer>
  );
}
